/* =====================================================================================
   Support: the customer case layer. The record is the timeline; the wall between an
   internal note and a reply the customer sees is the visible_to_customer flag, set at
   the moment of writing, never retrofitted. Complaints carry their reportability
   decision, and the Quality bridge raises a linked nonconformance rather than a copy.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export interface Follower { id: string; member_id: string }

export interface SupportCase {
  id: string; number: string; kind: string; severity: string; status: string;
  title: string; description: string | null; source: string;
  account_id: string | null; portal_user_id: string | null;
  contact_name: string | null; contact_email: string | null;
  product_id: string | null; lot_id: string | null; order_id: string | null;
  assigned_to: string | null; opened_at: string; acknowledged_at: string | null;
  resolved_at: string | null; closed_at: string | null; resolution: string | null;
  investigation_summary: string | null; vigilance_reportable: boolean | null;
  vigilance_rationale: string | null; vigilance_assessed_at: string | null;
  nonconformance_id: string | null; created_by: string | null;
  followup_of_case_id: string | null;
}
export interface CaseEvent {
  id: string; case_id: string; kind: string; body: string;
  visible_to_customer: boolean; portal_user_id: string | null;
  created_by: string | null; created_at: string;
}

export const CASE_KINDS: [string, string][] = [
  ['complaint', 'Complaint'],
  ['non_conformity', 'Non-conformity'],
  ['feature_request', 'Feature request'],
  ['tech_support', 'Tech support'],
];
export const CASE_SEVERITIES: [string, string][] = [
  ['critical', 'Critical'], ['high', 'High'], ['normal', 'Normal'], ['low', 'Low'],
];
export const CASE_STATUSES: [string, string][] = [
  ['new', 'New'], ['acknowledged', 'Acknowledged'], ['in_progress', 'In progress'],
  ['waiting_customer', 'Waiting on customer'], ['resolved', 'Resolved'], ['closed', 'Closed'],
];
export const CASE_SOURCES: [string, string][] = [
  ['phone', 'Phone'], ['email', 'Email'], ['field', 'Field visit'], ['internal', 'Internal'],
];

async function sb() { const { supabase } = await import('./supabase'); return supabase(); }

export async function fetchCases(accountId?: string): Promise<SupportCase[]> {
  if (!live()) return [];
  let q = (await sb()).schema('crm').from('support_cases').select('*');
  if (accountId) q = q.eq('account_id', accountId);
  const { data, error } = await q.order('opened_at', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the cases'));
  return (data ?? []) as SupportCase[];
}

export async function createCase(c: { kind: string; severity: string; title: string;
  description?: string; source: string; accountId?: string; contactName?: string;
  contactEmail?: string; productId?: string; lotId?: string; orderId?: string }): Promise<string> {
  const s = await sb();
  const { data: org } = await s.schema('app').rpc('current_org_id');
  const { data: number, error: eN } = await s.schema('app')
    .rpc('next_number', { p_org_id: org, p_series: 'case' });
  if (eN) throw new Error(explain(eN.message, 'take the next case number'));
  const { data: me } = await s.schema('app').rpc('current_member_id');
  const { data, error } = await s.schema('crm').from('support_cases').insert({
    number, kind: c.kind, severity: c.severity, title: c.title,
    description: c.description || null, source: c.source,
    account_id: c.accountId || null, contact_name: c.contactName || null,
    contact_email: c.contactEmail || null, product_id: c.productId || null,
    lot_id: c.lotId || null, order_id: c.orderId || null, created_by: me ?? null,
  }).select('number').single();
  if (error) throw new Error(explain(error.message, 'open the case'));
  return data.number as string;
}

export async function updateCase(id: string, patch: Partial<SupportCase>) {
  const { error } = await (await sb()).schema('crm').from('support_cases')
    .update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the case'));
}

export async function assessVigilance(id: string, reportable: boolean, rationale: string) {
  const s = await sb();
  const { data: me } = await s.schema('app').rpc('current_member_id');
  const { error } = await s.schema('crm').from('support_cases').update({
    vigilance_reportable: reportable, vigilance_rationale: rationale,
    vigilance_assessed_by: me ?? null, vigilance_assessed_at: new Date().toISOString(),
  }).eq('id', id);
  if (error) throw new Error(explain(error.message, 'record the reportability decision'));
}

export async function fetchCaseEvents(caseId: string): Promise<CaseEvent[]> {
  const { data, error } = await (await sb()).schema('crm').from('support_case_events')
    .select('*').eq('case_id', caseId).order('created_at');
  if (error) throw new Error(explain(error.message, 'read the case timeline'));
  return (data ?? []) as CaseEvent[];
}

export async function addCaseEvent(caseId: string, body: string, visibleToCustomer: boolean) {
  const s = await sb();
  const { data: me } = await s.schema('app').rpc('current_member_id');
  const { error } = await s.schema('crm').from('support_case_events').insert({
    case_id: caseId, kind: visibleToCustomer ? 'reply_to_customer' : 'note',
    body, visible_to_customer: visibleToCustomer, created_by: me ?? null,
  });
  if (error) throw new Error(explain(error.message, 'add to the timeline'));
}

export async function raiseNonconformance(caseId: string): Promise<string> {
  const { data, error } = await (await sb()).schema('crm')
    .rpc('raise_case_nonconformance', { p_case: caseId });
  if (error) throw new Error(explain(error.message, 'raise the nonconformance'));
  return data as string;
}

export async function fetchNcRef(ncId: string): Promise<string | null> {
  const { data } = await (await sb()).schema('quality').from('nonconformances')
    .select('reference').eq('id', ncId).single();
  return (data?.reference as string) ?? null;
}


/* ---------------------------------------------------------------- followers */
export async function fetchFollowers(caseId: string): Promise<Follower[]> {
  const { data, error } = await (await sb()).schema('crm').from('support_case_followers')
    .select('id, member_id').eq('case_id', caseId);
  if (error) throw new Error(explain(error.message, 'read the followers'));
  return (data ?? []) as Follower[];
}
export async function addFollower(caseId: string, memberId: string) {
  const s = await sb();
  const { data: me } = await s.schema('app').rpc('current_member_id');
  const { error } = await s.schema('crm').from('support_case_followers')
    .insert({ case_id: caseId, member_id: memberId, added_by: me ?? null });
  if (error) throw new Error(explain(error.message, 'add the follower'));
}
export async function removeFollower(id: string) {
  const { error } = await (await sb()).schema('crm').from('support_case_followers')
    .delete().eq('id', id);
  if (error) throw new Error(explain(error.message, 'remove the follower'));
}

/* ---------------------------------------------------------------- follow-up chain */
export async function createFollowupCase(caseId: string): Promise<string> {
  const { data, error } = await (await sb()).schema('crm')
    .rpc('create_followup_case', { p_case: caseId });
  if (error) throw new Error(explain(error.message, 'raise the follow-up case'));
  return data as string;
}

/* ---------------------------------------------------------------- email updates
   Fire-and-forget: the ticket's people get an email about what just happened. On a
   project without mail configured the function simply reports it skipped. */
export async function notifyCase(caseId: string, what: string) {
  try {
    const { supabase } = await import('./supabase');
    await supabase().functions.invoke('case-notify', { body: { caseId, what } });
  } catch { /* email is best-effort; Chorus already carries the update */ }
}
